import { createRouter, createWebHistory } from 'vue-router'
import firebase from 'firebase'
import db from '@/firebase/init'
import store from '@/store'
// Locale helpers
import { setSiteLocale, getSavedSiteLocale } from '@/helpers/siteLocale'
import { journeyVerboseLog } from '@/helpers/journeyVerboseLog'
import { assignSignInChurch } from '@/helpers/userAccess'
import { i18n, changeLocale } from '@/plugins/i18n'
import {
  resolveEffectiveActive,
} from '@/helpers/licenseExpiry'
import {
  localizedNamedLocation,
  routeBaseName,
} from '@/helpers/localePath'

import Signup from '@/components/auth/Signup'
import Login from '@/components/auth/Login'
import License from '@/components/auth/License'
import Archived from '@/components/auth/Archived'
import Maintenance from '@/components/auth/Maintenance'
import Home from '@/components/layout/Home'
import Welcome from '@/components/layout/Welcome'
import Unit from '@/components/layout/Unit'
import PreviewUnit from '@/components/layout/PreviewUnit'
import PreviewWeek from '@/components/layout/PreviewWeek'
import PreviewSection from '@/components/layout/PreviewSection'
import PreviewMission from '@/components/layout/PreviewMission'
import Pdf from '@/components/layout/Pdf'
import SectionPdf from '@/components/layout/SectionPdf'
import CoursePdf from '@/components/layout/CoursePdf'
import UnitPdf from '@/components/layout/UnitPdf'
import Dashboard from '@/components/layout/Dashboard'
import Faqs from '@/components/admin/Faqs'
import Courses from '@/components/admin/Courses'
import Sections from '@/components/admin/Sections'
import MissionsBuilder from '@/components/admin/MissionsBuilder'
import Weeks from '@/components/admin/Weeks'
import Units from '@/components/admin/Units'
import AppGuide from '@/components/admin/AppGuide'
import JourneyOrient from '@/components/admin/JourneyOrient'
import Messages from '@/components/admin/Messages'
import Tutorials from '@/components/admin/Tutorials'
import Options from '@/components/admin/Options'
import StripeKey from '@/components/admin/Stripe'
import ErrorsLog from '@/components/admin/ErrorsLog'
import Feedback from '@/components/admin/Feedback'
import Notifications from '@/components/admin/Notifications'
import Users from '@/components/admin/Users'
import PendingUsers from '@/components/admin/PendingUsers'
import Groups from '@/components/admin/Groups'
import Churches from '@/components/admin/Churches'
import ApiKey from '@/components/admin/ApiKey'
import NewFeatures from '@/components/admin/NewFeatures'
import TermsAdmin from '@/components/admin/TermsAdmin'
import TermsAcceptance from '@/components/layout/TermsAcceptance'
import ViewTerms from '@/components/layout/ViewTerms'
import PrivacyPolicy from '@/components/admin/PrivacyPolicy'
import AdminLanguages from '@/components/admin/AdminLanguages'
import UITranslations from '@/components/admin/UITranslations'
import LanguageTranslations from '@/components/admin/LanguageTranslations'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: true, roles: ['member', 'admin'] },
  },
  {
    path: '/welcome',
    name: 'Welcome',
    component: Welcome,
    meta: { requiresAuth: true, roles: ['member', 'admin'] },
  },
  {
    path: '/unit/:courseID?/:sectionID?/:id?',
    name: 'Unit',
    component: Unit,
    meta: {
      requiresAuth: true,
      roles: ['member', 'superAdmin', 'helpDesk', 'admin'],
    },
  },
  {
    path: '/preview',
    name: 'PreviewUnit',
    component: PreviewUnit,
    props: true,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/previewweek',
    name: 'PreviewWeek',
    component: PreviewWeek,
    props: true,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/previewsection',
    name: 'PreviewSection',
    component: PreviewSection,
    props: true,
    meta: {
      requiresAuth: true,
      roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
    },
  },
  {
    path: '/previewmission',
    name: 'PreviewMission',
    component: PreviewMission,
    props: true,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/pdf/:courseID?/:sectionID?/:id?',
    name: 'Pdf',
    component: Pdf,
    meta: {
      requiresAuth: true,
      roles: ['member', 'superAdmin', 'helpDesk', 'admin'],
    },
  },
  {
    path: '/sectionpdf/:courseID?/:sectionID?',
    name: 'SectionPdf',
    component: SectionPdf,
    meta: {
      requiresAuth: true,
      roles: ['member', 'superAdmin', 'helpDesk', 'admin'],
    },
  },
  {
    path: '/coursepdf/:courseID?',
    name: 'CoursePdf',
    component: CoursePdf,
    meta: {
      requiresAuth: true,
      roles: ['member', 'superAdmin', 'helpDesk', 'admin'],
    },
  },
  {
    path: '/unitpdf/:courseID?/:sectionID?/:unitID?',
    name: 'UnitPdf',
    component: UnitPdf,
    meta: {
      requiresAuth: true,
      roles: ['member', 'superAdmin', 'helpDesk', 'admin'],
    },
  },
  {
    path: '/signup',
    name: 'Signup',
    component: Signup,
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  { path: '/login', name: 'Login', component: Login },
  { path: '/maintenance', name: 'Maintenance', component: Maintenance },
  { path: '/license/:action?/:token?/', name: 'License', component: License },
  { path: '/archived', name: 'Archived', component: Archived },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      requiresAuth: true,
      roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
    },
  },
  {
    path: '/guide',
    name: 'AppGuide',
    component: AppGuide,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/orientation',
    name: 'JourneyOrient',
    component: JourneyOrient,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/messages',
    name: 'Messages',
    component: Messages,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/tutorials',
    name: 'Tutorials',
    component: Tutorials,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/options',
    name: 'Options',
    component: Options,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/stripe',
    name: 'StripeKey',
    component: StripeKey,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/errors',
    name: 'ErrorsLog',
    component: ErrorsLog,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/feedback',
    name: 'Feedback',
    component: Feedback,
    meta: {
      requiresAuth: true,
      roles: ['superAdmin', 'helpDesk', 'owner'],
    },
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: Notifications,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/groups',
    name: 'Groups',
    component: Groups,
    meta: {
      requiresAuth: true,
      roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
    },
  },
  {
    path: '/users',
    name: 'Users',
    component: Users,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/pendingusers',
    name: 'PendingUsers',
    component: PendingUsers,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/faqs',
    name: 'Faqs',
    component: Faqs,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/missions',
    name: 'MissionsBuilder',
    component: MissionsBuilder,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/weeks',
    name: 'Weeks',
    component: Weeks,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/courses',
    name: 'Courses',
    component: Courses,
    meta: {
      requiresAuth: true,
      roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
    },
  },
  {
    path: '/sections',
    name: 'Sections',
    component: Sections,
    meta: {
      requiresAuth: true,
      roles: ['superAdmin', 'helpDesk', 'admin', 'leader'],
    },
  },
  {
    path: '/units',
    name: 'Units',
    component: Units,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/churches',
    name: 'Churches',
    component: Churches,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/api',
    name: 'ApiKey',
    component: ApiKey,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk', 'admin'] },
  },
  {
    path: '/newfeatures',
    name: 'NewFeatures',
    component: NewFeatures,
    meta: { requiresAuth: true, roles: ['owner'] },
  },
  {
    path: '/terms',
    name: 'TermsAdmin',
    component: TermsAdmin,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/view-terms',
    name: 'ViewTerms',
    component: ViewTerms,
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  {
    path: '/terms-acceptance',
    name: 'TermsAcceptance',
    component: TermsAcceptance,
    meta: { requiresAuth: true, roles: ['admin'] },
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
  {
    path: '/languages',
    name: 'AdminLanguages',
    component: AdminLanguages,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/ui-translations',
    name: 'UITranslations',
    component: UITranslations,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
  {
    path: '/languages/:code/translations',
    name: 'LanguageTranslations',
    component: LanguageTranslations,
    meta: { requiresAuth: true, roles: ['superAdmin', 'helpDesk'] },
  },
]

// === Locale-prefixed route branch (generic) ===
// We mirror the existing routes under "/:locale" WITHOUT hand-duplicating everything.
// Child paths are the same as your existing absolute paths, but without the leading slash.
const DEFAULT_LOCALE = 'en'
const stripLeadingSlash = (p) => (p.startsWith('/') ? p.slice(1) : p)

// Cache for enabled languages to avoid repeated DB calls
let enabledLanguagesCache = null
let cacheTimestamp = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
let maintenanceCache = null
let maintenanceCacheTimestamp = 0
const MAINTENANCE_CACHE_DURATION = 15 * 1000

// Track navigation to prevent infinite loops
let navigationStack = new Set()

// Function to get enabled languages from database
async function getEnabledLanguages() {
  const now = Date.now()

  // Return cached data if still valid
  if (enabledLanguagesCache && now - cacheTimestamp < CACHE_DURATION) {
    return enabledLanguagesCache
  }

  try {
    const doc = await db.collection('config').doc('languages').get()
    if (doc.exists) {
      const data = doc.data()
      const enabledLangs = data.enabledLangs || []
      const defaultLang = data.defaultLang || 'en'

      const enabled = enabledLangs
        .filter((lang) => lang.enabled !== false)
        .map((lang) => String(lang.code || lang).toLowerCase())
        .filter(Boolean)

      const defaultLocale = String(defaultLang).toLowerCase()

      enabledLanguagesCache = { enabled, defaultLocale }
      cacheTimestamp = now

      // console.log('[router] Loaded enabled languages from DB:', enabled)
      return { enabled, defaultLocale }
    }
  } catch (error) {
    console.warn('[router] Error loading languages from DB:', error)
  }

  // Fallback to hardcoded languages
  const fallback = {
    enabled: ['en', 'pt', 'es'],
    defaultLocale: DEFAULT_LOCALE,
  }
  journeyVerboseLog('[router] Using fallback languages:', fallback.enabled)
  return fallback
}

async function getMaintenanceConfig() {
  const now = Date.now()
  if (
    maintenanceCache &&
    now - maintenanceCacheTimestamp < MAINTENANCE_CACHE_DURATION
  ) {
    return maintenanceCache
  }

  const fallback = {
    appMaintenance: false,
    featureMaintenance: false,
    maintenanceEnabled: false,
    allowedUids: [],
    allowedRoles: [],
  }

  try {
    const [doc, featureSnapshot] = await Promise.all([
      db.collection('config').doc('maintenance').get(),
      db
        .collection('newfeatures')
        .where('slug', '==', 'maintenance-mode')
        .limit(1)
        .get(),
    ])

    const data = doc.exists ? doc.data() || {} : {}
    const allowedUids = Array.isArray(data.allowedUids)
      ? data.allowedUids.filter((v) => typeof v === 'string' && v.trim())
      : []
    const allowedRoles = Array.isArray(data.allowedRoles)
      ? data.allowedRoles
          .filter((v) => typeof v === 'string' && v.trim())
          .map((v) => String(v).trim().toLowerCase())
      : []
    const appMaintenance = data.appMaintenance === true
    const featureData = featureSnapshot.empty
      ? null
      : featureSnapshot.docs[0].data() || {}
    const featureDocExists = !!featureData
    const featureMaintenance = featureData?.active === true
    // If maintenance-mode exists in newfeatures, it becomes the canonical toggle.
    // Otherwise, fall back to legacy config.appMaintenance behavior.
    const maintenanceEnabled = featureDocExists ? featureMaintenance : appMaintenance

    const parsed = {
      appMaintenance,
      featureMaintenance,
      maintenanceEnabled,
      allowedUids,
      allowedRoles,
    }

    maintenanceCache = parsed
    maintenanceCacheTimestamp = now
    return parsed
  } catch (error) {
    console.warn('[router] Error loading maintenance config:', error)
    maintenanceCache = fallback
    maintenanceCacheTimestamp = now
    return fallback
  }
}

// Shallow clone each route for children; keep meta/props/name, etc.
const localeChildren = routes
  // Do not include the existing catch-all redirect route as a child
  .filter((r) => r.path !== '/:pathMatch(.*)*')
  .map((r) => ({
    ...r,
    path: stripLeadingSlash(r.path), // '/welcome' -> 'welcome'
    name: r.name ? `${r.name}__loc` : undefined, // keep names unique
    meta: { ...(r.meta || {}), baseName: r.name || null }, // <-- add original name
  }))

// Add the generic parent for all non-default locales
const localeParent = {
  path: '/:locale',
  children: localeChildren,
}

// Final routes array: keep your originals (English unprefixed), then the generic parent, then your catch-all
const finalRoutes = [
  ...routes,
  localeParent,
  // Preserve your original catch-all redirect at the very end
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: finalRoutes,
  scrollBehavior: () => ({ left: 0, top: 0 }),
})

router.beforeEach(async (to, from, next) => {
  // Check for email link sign-in on the target navigation URL.
  // Using to.fullPath avoids stale window.location values during redirects.
  const targetAbsoluteUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${to.fullPath}`
      : ''
  if (
    targetAbsoluteUrl &&
    firebase.auth().isSignInWithEmailLink(targetAbsoluteUrl)
  ) {
    await store.dispatch('UserData/signInWithEmailLink', {
      emailLink: targetAbsoluteUrl,
    })
    // Normalize to /login without query so the one-time code is not re-consumed.
    if (to.path !== '/login' || (to.query && Object.keys(to.query).length > 0)) {
      next('/login')
      return
    }
  }

  // Close learner course drawer before paint on print/PDF routes (avoids drawer flash).
  const routeName = to.name && String(to.name)
  const routeNameBase = routeName?.endsWith('__loc')
    ? routeName.slice(0, -5)
    : routeName
  if (
    routeNameBase === 'SectionPdf' ||
    routeNameBase === 'UnitPdf' ||
    routeNameBase === 'Pdf' ||
    routeNameBase === 'CoursePdf'
  ) {
    store.dispatch('AppData/courseDrawer', false)
  }

  // console.log('[router] beforeEach triggered:', {
  //   to: to.path,
  //   from: from.path,
  //   paramLocale: to.params?.locale,
  // })

  // Create a unique key for this navigation
  const navigationKey = `${to.path}-${to.params?.locale || 'default'}`

  // Prevent infinite loops by checking if we're already processing this exact navigation
  if (navigationStack.has(navigationKey)) {
    journeyVerboseLog(
      '[router] Preventing infinite loop for navigation:',
      navigationKey
    )
    return next()
  }

  // Add current navigation to stack
  navigationStack.add(navigationKey)

  // Clean up navigation stack after a delay
  setTimeout(() => {
    navigationStack.delete(navigationKey)
  }, 1000)

  // === Locale handling (runs before auth checks) ===
  const paramLocale = to.params?.locale
    ? String(to.params.locale).toLowerCase()
    : null

  // Load languages from DB synchronously
  let enabled = ['en', 'pt', 'es'] // fallback
  let defaultLocale = 'en' // fallback

  try {
    const result = await getEnabledLanguages()
    enabled = result.enabled
    defaultLocale = result.defaultLocale
    // console.log('[router] Loaded enabled languages from DB:', enabled)
  } catch (error) {
    console.error('[router] Error loading languages from DB:', error)
  }

  // --- Root "/" redirect to saved locale if enabled & not default ---
  // Only when landing exactly on "/" and without a :locale param.
  if (!paramLocale && to.path === '/') {
    const saved = (getSavedSiteLocale() || '').toLowerCase()
    if (saved && enabled.includes(saved) && saved !== defaultLocale) {
      // Keep path "/" but prefix with the saved locale (e.g., "/pt/")
      // Preserve any search/hash if present (unlikely on root, but safe).
      const suffix = to.fullPath.length > 1 ? to.fullPath : '/'
      return next(`/${saved}${suffix}`)
    }
  }

  // Unprefixed URL + saved non-default locale → /:locale/... (e.g. /login → /pt/login)
  if (!paramLocale && to.path !== '/') {
    const saved = (getSavedSiteLocale() || '').toLowerCase()
    if (saved && enabled.includes(saved) && saved !== defaultLocale) {
      const baseName = to.name
      if (baseName && typeof baseName === 'string' && !baseName.endsWith('__loc')) {
        const locName = `${baseName}__loc`
        if (router.hasRoute(locName)) {
          return next({
            name: locName,
            params: { locale: saved, ...to.params },
            query: to.query,
            hash: to.hash,
            replace: true,
          })
        }
      }
    }
  }

  if (paramLocale) {
    // Visiting a prefixed route
    if (!enabled.includes(paramLocale)) {
      // Unknown/disabled locale -> go to default (unprefixed path)
      const unprefixed =
        to.fullPath.replace(new RegExp(`^/${paramLocale}(?=/|$)`), '') || '/'
      setSiteLocale(defaultLocale)
      return next(unprefixed)
    }
    // Valid locale -> apply it (i18n, moment, dir, localStorage)
    // Check if locale is already set to avoid unnecessary changes
    const currentLocale = i18n.global.locale.value
    if (currentLocale !== paramLocale) {
      // Don't set i18n locale here - let changeLocale handle it
      setSiteLocale(paramLocale, { skipI18n: true })

      // Change locale and load translations asynchronously
      try {
        await changeLocale(paramLocale)
      } catch (error) {
        console.error('[router] Error changing locale:', error)
      }
    }
  } else {
    // Unprefixed route: always honor saved site language (e.g. geo / user choice) when enabled.
    // Previously, routes that also exist under /:locale/ (e.g. /dashboard) forced defaultLocale
    // and overwrote a saved "pt" with "en" after login.
    const gl = i18n.global
    const live =
      gl && gl.locale && typeof gl.locale === 'object' && 'value' in gl.locale
        ? gl.locale.value
        : gl.locale
    const currentLocale = live ? String(live).toLowerCase() : null

    const saved = (getSavedSiteLocale() || '').toLowerCase()
    if (saved && enabled.includes(saved)) {
      if (currentLocale !== saved) {
        setSiteLocale(saved, { skipI18n: true })
        try {
          await changeLocale(saved)
        } catch (error) {
          console.error('[router] Error changing locale:', error)
        }
      }
    } else if (!currentLocale || currentLocale !== defaultLocale) {
      setSiteLocale(defaultLocale)
    }
  }

  // Continue with navigation after locale handling
  if (to.path === '/__/auth/action') {
    return next({ name: 'Login' })
  }

  if (to.meta.requiresAuth) {
    // Wrap onAuthStateChanged in a Promise to make it awaitable
    await new Promise((resolve) => {
      firebase.auth().onAuthStateChanged(async (user) => {
        if (!user) {
          next({ name: 'Login' })
          resolve()
          return
        }

        if (!user.emailVerified) {
          store.dispatch('UserData/setLoginFeedback', {
            code: 'resubmit-verification',
            message: 'Your email is not verified',
          })
          next({ name: 'Login' })
          resolve()
          return
        }

        if (
          to.fullPath.startsWith('/pdf') ||
          to.fullPath.startsWith('/sectionpdf') ||
          to.fullPath.startsWith('/unitpdf') ||
          to.fullPath.startsWith('/coursepdf') ||
          to.path.includes('/sectionpdf') ||
          to.path.includes('/unitpdf') ||
          to.path.includes('/coursepdf')
        ) {
          next()
          resolve()
          return
        }

        try {
          const userSnapshot = await db
            .collection('users')
            .where('email', '==', user.email)
            .get()

          if (userSnapshot.empty) {
            next({ name: 'Login' })
            resolve()
            return
          }

          const currentuser = userSnapshot.docs[0].data()
          const userDocId = userSnapshot.docs[0].id

          const effectiveActive = resolveEffectiveActive(currentuser)
          // Redirect-only: never persist deactivation here (login + nightly job do that).
          const routedUser = { ...currentuser, active: effectiveActive }

          let resolvedCid = currentuser.cid || null
          if (
            !resolvedCid &&
            Array.isArray(currentuser.roles) &&
            currentuser.roles.includes('admin') &&
            user.email
          ) {
            // Only the server sets a church (GitHub #147); it matches the
            // church's admin email.
            resolvedCid = await assignSignInChurch()
          }

          const maintenanceConfig = await getMaintenanceConfig()
          const currentUid = user.uid || currentuser.uid || null
          const currentRoles = Array.isArray(currentuser.roles)
            ? currentuser.roles
                .filter((v) => typeof v === 'string' && v.trim())
                .map((v) => String(v).trim().toLowerCase())
            : []
          const hasRoleAccess = currentRoles.some((role) =>
            maintenanceConfig.allowedRoles.includes(role)
          )
          const hasOwnerBypass = currentRoles.includes('owner')
          const isAllowlisted =
            !!currentUid && maintenanceConfig.allowedUids.includes(currentUid)
          const hasMaintenanceAccess =
            isAllowlisted || hasRoleAccess || hasOwnerBypass

          const destName = routeBaseName(to)

          if (
            maintenanceConfig.maintenanceEnabled &&
            !hasMaintenanceAccess &&
            destName !== 'Maintenance'
          ) {
            const localePrefix = to.params?.locale
              ? `/${String(to.params.locale).toLowerCase()}`
              : ''
            next(`${localePrefix}/maintenance`)
            resolve()
            return
          }

          // ✅ Always commit Firestore user to Vuex
          store.commit('UserData/updateUser', {
            cid: resolvedCid,
            email: currentuser.email,
            emailVerified: user.emailVerified,
            uid: currentuser.uid,
            url: currentuser.url,
            user_name: currentuser.user_name,
            roles: currentuser.roles,
            phoneNumber: currentuser.phoneNumber,
            possibleLeader: currentuser.possibleLeader,
            profileComplete: currentuser.profileComplete,
            fullPath: currentuser.fullPath,
            joined: currentuser.joined,
            active: routedUser.active,
            activated: routedUser.activated,
            archived: routedUser.archived,
            id: userDocId,
          })

          // Scope AppData to the user's church on first entry (API-created admins skip FirebaseUI/userLogin).
          // superAdmin/helpDesk are exempt: they roam across churches via the NavBar selector, so the
          // guard must never overwrite their selection. For everyone else, only hydrate when no church is
          // already selected, so later navigations don't reset the active church.
          const canRoamChurches =
            currentRoles.includes('superadmin') ||
            currentRoles.includes('helpdesk')
          const hasSelectedChurch = !!store.getters['AppData/cid']
          if (resolvedCid && !canRoamChurches && !hasSelectedChurch) {
            await store.dispatch(
              'AppData/hydrateChurchContextFromCid',
              resolvedCid,
              { root: true }
            )
          }

          if (
            currentuser.roles.includes('admin') &&
            destName !== 'TermsAcceptance'
          ) {
            const termsDoc = await db.collection('config').doc('terms').get()
            const latestVersion = termsDoc.exists
              ? termsDoc.data().latestVersion
              : 1

            if (
              !currentuser.termsAcceptedVersion ||
              Number(currentuser.termsAcceptedVersion) < latestVersion
            ) {
              // Preserve locale when redirecting to terms acceptance
              const locale = to.params?.locale
              const redirectPath = locale
                ? `/${locale}/terms-acceptance`
                : '/terms-acceptance'
              next(redirectPath)
              resolve()
              return
            }
          }

          const canSkipChurchPin =
            currentRoles.includes('superadmin') ||
            currentRoles.includes('helpdesk')
          if (!resolvedCid && !canSkipChurchPin && destName !== 'Home') {
            next(localizedNamedLocation('Home', to))
            resolve()
            return
          }

          if (
            !routedUser.active &&
            !currentuser.roles.includes('admin') &&
            !currentuser.roles.includes('superAdmin') &&
            !currentuser.roles.includes('helpDesk')
          ) {
            if (destName !== 'Home' && destName !== 'License') {
              next(localizedNamedLocation('License', to))
              resolve()
              return
            }
          }

          if (
            currentuser.archived &&
            !['admin', 'superAdmin', 'helpDesk'].some((role) =>
              currentuser.roles.includes(role)
            )
          ) {
            if (destName !== 'Home' && destName !== 'Archived') {
              next(localizedNamedLocation('Archived', to))
              resolve()
              return
            }
          }

          if (
            !to.meta.roles ||
            to.meta.roles.some((role) => currentuser.roles.includes(role))
          ) {
            next()
            resolve()
          } else {
            next({ name: 'Login' })
            resolve()
          }
        } catch (err) {
          console.error('Auth error:', err)
          next({ name: 'Login' })
          resolve()
        }
      })
    })
  } else {
    next()
  }
})

// Clean up navigation stack on route changes
router.afterEach(() => {
  // Clean up any lingering navigation entries after successful navigation
  setTimeout(() => {
    navigationStack.clear()
  }, 500)
})

export default router
